/**
 * Commercial quote builder.
 *
 * The form is composed from theme-editor blocks, so nothing here knows what the
 * questions are. Fields declare themselves in markup:
 *
 *   data-field="<hubspot property>"   the property the answer maps to
 *   data-multi                        chip group or checkbox list, keeps an array
 *   data-required                     must be answered before the step advances
 *   data-summary                      include in the running summary
 *
 * Answers are keyed by HubSpot property name throughout, so there is no
 * translation layer between what the buyer answers and what lands in the CRM.
 */
class CommercialQuoteBuilder extends HTMLElement {
  constructor() {
    super();
    this.form = this.querySelector('[data-qb-form]');
    this.steps = Array.from(this.querySelectorAll('[data-step]'));
    this.done = this.querySelector('[data-qb-done]');
    this.summary = this.querySelector('[data-qb-summary-text]');
    this.fallbackForm = this.querySelector('#CommercialQuoteFallback');
    this.portalId = this.dataset.portalId || '';
    this.formGuid = this.dataset.formGuid || '';
    this.leadFormGuid = this.dataset.leadFormGuid || '';
    this.leadSent = '';
    // The hidden property that records which form a submission came from.
    this.entryField = this.dataset.entryField || 'fold_entry_form';
    this.errorMessage = this.dataset.errorMessage || '';
    this.overflowProperty = this.dataset.overflowProperty || '';
    this.region = this.dataset.region || 'na1';
    this.mobileSingle = this.hasAttribute('data-mobile-single');
    this.phone = window.matchMedia('(max-width: 749px)');
    this.sub = 0;
    this.index = 0;
    this.dialog = this.querySelector('[data-qb-dialog]');
    this.answers = {};
  }

  connectedCallback() {
    if (!this.form || !this.steps.length) return;

    this.hydrateDefaults();
    if (this.mobileSingle) {
      this.applySubSteps = this.applySubSteps.bind(this);
      this.phone.addEventListener('change', this.applySubSteps);
      this.applySubSteps();
    }
    if (this.dialog) this.setupDialog();
    this.addEventListener('click', this.onClick.bind(this));
    this.addEventListener('change', this.onChange.bind(this));
    this.addEventListener('input', this.onInput.bind(this));
    this.form.addEventListener('submit', this.onSubmit.bind(this));
  }

  /**
   * Popup mode.
   *
   * Uses a native <dialog> so the focus trap, Escape handling and page inerting
   * come from the platform. Every existing "#quote" link on the page opens it
   * too, since in popup mode there is nothing to scroll to.
   */
  setupDialog() {
    const open = () => {
      if (!this.dialog.open) this.dialog.showModal();
      document.body.classList.add('c-modal-open');
    };
    const close = () => {
      if (this.dialog.open) this.dialog.close();
    };

    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-qb-open]')) { event.preventDefault(); open(); }
      if (event.target.closest('[data-qb-close]')) { event.preventDefault(); close(); }
    });

    // Clicking the backdrop closes: the dialog element itself is the backdrop
    // area, its inner panel is not, so a click landing on the dialog is outside.
    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog) close();
    });

    // Chrome 155 was seen firing the dialog's toggle event but never its close
    // event, which left the page scroll-locked. Either one releases it.
    const release = () => { if (!this.dialog.open) document.body.classList.remove('c-modal-open'); };
    this.dialog.addEventListener('close', release);
    this.dialog.addEventListener('toggle', release);

    // Any quote link anywhere on the page should open the form rather than
    // jump to an anchor that is now inside a closed dialog.
    const anchor = '#' + (this.id || 'quote');
    document.addEventListener('click', (event) => {
      const link = event.target.closest('a[href]');
      if (!link) return;
      const href = link.getAttribute('href') || '';
      if (href === anchor || href.endsWith(anchor)) {
        event.preventDefault();
        open();
      }
    });
  }

  /**
   * Copy every typed field's current value into answers.
   *
   * Answers are normally kept up to date by input events, but a browser can
   * put values back without firing any (autofill, or restoring the page after
   * the buyer follows the privacy link and comes back). The fields then look
   * filled while answers is empty, so the DOM is read again before anything is
   * checked or sent.
   */
  syncTextAnswers() {
    this.querySelectorAll('input[data-field], textarea[data-field]').forEach((el) => {
      if (el.type === 'checkbox' || el.type === 'hidden') return;
      this.answers[el.dataset.field] = el.value.trim();
    });
  }

  /**
   * Seed answers from the markup's initial state.
   *
   * Only a `change` event writes to `answers`, so anything pre-checked in the
   * customizer would otherwise never be recorded. The finance toggle ships on
   * by default, and dropping it would lose the most valuable qualifier here.
   */
  hydrateDefaults() {
    this.querySelectorAll('input[type="checkbox"][data-field]').forEach((input) => {
      if (input.hasAttribute('data-multi')) {
        if (input.checked) this.push(input.dataset.field, input.value);
      } else {
        this.answers[input.dataset.field] = input.checked
          ? input.value
          : input.dataset.valueOff || '';
      }
    });

    this.querySelectorAll('.c-chip[aria-pressed="true"][data-field]').forEach((chip) => {
      this.setChipAnswer(chip, true);
    });

    this.querySelectorAll('input[type="hidden"][data-field]').forEach((input) => {
      if (input.hasAttribute('data-auto-reference')) {
        input.value = this.reference;
      }
      if (input.value) this.answers[input.dataset.field] = input.value;
    });
  }

  /**
   * A short human-readable reference, shown on the confirmation screen and
   * stored on the record so a caller can quote it back. Generated once per
   * page view, not per submission, so what the buyer sees is what was sent.
   */
  get reference() {
    if (!this._reference) {
      this._reference = 'FC-' + String(Math.floor(1000 + Math.random() * 9000));
    }
    return this._reference;
  }

  push(field, value) {
    if (!Array.isArray(this.answers[field])) this.answers[field] = [];
    if (!this.answers[field].includes(value)) this.answers[field].push(value);
  }

  pull(field, value) {
    if (!Array.isArray(this.answers[field])) return;
    this.answers[field] = this.answers[field].filter((item) => item !== value);
  }

  /* ----------------------------------------------------------- interaction */

  onClick(event) {
    const chip = event.target.closest('.c-chip');
    if (chip && this.contains(chip)) return this.onChip(chip);

    if (event.target.closest('[data-qb-next]')) return this.next();
    if (event.target.closest('[data-qb-back]')) return this.back();
  }

  onChip(chip) {
    const field = chip.dataset.field;
    const multi = chip.hasAttribute('data-multi');

    if (multi) {
      const pressed = chip.getAttribute('aria-pressed') === 'true';
      chip.setAttribute('aria-pressed', String(!pressed));
      this.setChipAnswer(chip, !pressed);
    } else {
      this.querySelectorAll(`.c-chip[data-field="${CSS.escape(field)}"]`).forEach((sibling) => {
        sibling.setAttribute('aria-pressed', String(sibling === chip));
      });
      this.setChipAnswer(chip, true);
    }

    this.clearError();
  }

  setChipAnswer(chip, on) {
    const multi = chip.hasAttribute('data-multi');
    const write = (field, value) => {
      if (!field) return;
      if (multi) {
        on ? this.push(field, value) : this.pull(field, value);
      } else {
        this.answers[field] = value;
      }
    };

    write(chip.dataset.field, chip.dataset.value);
    // A second property can capture the human-readable label, so the CRM keeps
    // both the number it can report on and the band the buyer actually chose.
    write(chip.dataset.labelField, chip.dataset.labelValue);

    // The summary can sit on the same step as the questions feeding it, so it
    // has to update on every answer rather than only when the step changes.
    this.renderSummary();
  }

  onChange(event) {
    const input = event.target;
    if (!input.dataset || !input.dataset.field) return;

    if (input.type === 'checkbox') {
      if (input.hasAttribute('data-multi')) {
        input.checked ? this.push(input.dataset.field, input.value) : this.pull(input.dataset.field, input.value);
      } else {
        this.answers[input.dataset.field] = input.checked
          ? input.value
          : input.dataset.valueOff || '';
      }
      this.renderSummary();
      return;
    }

    this.answers[input.dataset.field] = input.value.trim();
    this.renderSummary();
  }

  onInput(event) {
    if (event.target.dataset && event.target.dataset.field && event.target.type !== 'checkbox') {
      this.answers[event.target.dataset.field] = event.target.value.trim();
    }
  }

  /* ---------------------------------------------------------------- steps */

  get currentStep() {
    return this.steps[this.index];
  }

  /**
   * Questions inside the current step, treated as screens of their own on a
   * phone. A wall of chip groups is fine on a desktop column and miserable on
   * a 375px screen, so each question gets the full viewport instead.
   */
  get subScreens() {
    if (!this.mobileSingle || !this.phone.matches) return [];
    // The last step carries only Back and Send, so it stays one screen: split,
    // its later questions (upholstery) could never be reached on a phone.
    if (this.index >= this.steps.length - 1) return [];
    // A run of plain fields (name, email, phone, postcode) is one screen: the
    // contact details belong together, and splitting them read as a step with
    // one field and the rest missing. Each chip question is still a screen of
    // its own.
    const groups = [];
    let run = null;
    this.currentStep.querySelectorAll('.c-qb__block').forEach((block) => {
      if (block.classList.contains('c-qb__block--field')) {
        if (!run) { run = []; groups.push(run); }
        run.push(block);
      } else {
        run = null;
        groups.push([block]);
      }
    });
    return groups;
  }

  applySubSteps() {
    this.sub = 0;
    this.steps.forEach((step) => step.classList.toggle('c-qb__step--split', this.phone.matches));
    this.paintSubSteps();
  }

  paintSubSteps() {
    const screens = this.subScreens;
    // Off the phone breakpoint every question shows at once again.
    this.querySelectorAll('.c-qb__block').forEach((b) => b.removeAttribute('data-sub-hidden'));
    if (!screens.length) return;
    screens.forEach((group, i) => {
      if (i !== this.sub) group.forEach((block) => block.setAttribute('data-sub-hidden', ''));
    });
  }

  next() {
    this.syncTextAnswers();
    const screens = this.subScreens;
    if (screens.length && this.sub < screens.length - 1) {
      // Validate only what is on screen before moving on. Every block is
      // checked, not just up to the first failure, so all errors show at once.
      const valid = screens[this.sub].map((block) => this.validateScope(block)).every(Boolean);
      if (!valid) return;
      this.sub += 1;
      this.paintSubSteps();
      this.currentStep.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      return;
    }

    if (!this.validateStep()) return;
    if (this.index >= this.steps.length - 1) return;
    this.captureLead(this.currentStep);
    this.sub = 0;
    this.show(this.index + 1);
  }

  back() {
    if (this.subScreens.length && this.sub > 0) {
      this.sub -= 1;
      this.paintSubSteps();
      return;
    }
    if (this.index === 0) return;
    this.show(this.index - 1);
    // Back onto a step split into phone screens opens its last question, the
    // one just answered, not the first.
    const screens = this.subScreens;
    if (screens.length) {
      this.sub = screens.length - 1;
      this.paintSubSteps();
    }
  }

  show(nextIndex) {
    this.currentStep.hidden = true;
    this.currentStep.classList.remove('is-active');
    this.index = nextIndex;
    this.currentStep.hidden = false;
    this.currentStep.classList.add('is-active');

    this.sub = 0;
    this.paintSubSteps();
    this.renderSummary();
    this.currentStep.scrollIntoView({ behavior: 'smooth', block: 'nearest' });

    const focusable = this.currentStep.querySelector('.c-chip, input, textarea, button');
    if (focusable) focusable.focus({ preventScroll: true });
  }

  validateStep() {
    return this.validateScope(this.currentStep);
  }

  validateScope(step) {
    // Chip groups: one required marker per option, so dedupe by property.
    const chipFields = new Set(
      Array.from(step.querySelectorAll('.c-chip[data-required]')).map((chip) => chip.dataset.field)
    );
    for (const field of chipFields) {
      const answer = this.answers[field];
      const empty = Array.isArray(answer) ? !answer.length : !answer;
      if (empty) return this.showError(this.messageFor('choose'));
    }

    const inputs = Array.from(step.querySelectorAll('[data-required][data-field]'))
      .filter((el) => el.tagName === 'INPUT' || el.tagName === 'TEXTAREA');

    for (const input of inputs) {
      if (!input.value.trim()) {
        input.focus();
        return this.showError(this.messageFor('fields'));
      }
      if (input.type === 'email' && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(input.value.trim())) {
        input.focus();
        return this.showError(this.messageFor('email'));
      }
    }

    const consent = step.querySelector('input[data-required-consent]');
    if (consent && !consent.checked) return this.showError(this.messageFor('consent'));

    this.clearError();
    return true;
  }

  messageFor(kind) {
    return {
      choose: 'Choose an option in each group to continue.',
      fields: 'Fill in the required fields.',
      email: 'Check the email address.',
      consent: 'Tick the box to continue.',
    }[kind];
  }

  showError(message) {
    const box = this.currentStep.querySelector('[data-step-error]');
    if (box) {
      box.textContent = message;
      box.hidden = false;
    }
    return false;
  }

  clearError() {
    const box = this.currentStep && this.currentStep.querySelector('[data-step-error]');
    if (box) box.hidden = true;
  }

  renderSummary() {
    if (!this.summary) return;

    const fields = new Set(
      Array.from(this.querySelectorAll('[data-summary][data-field]')).map((el) => el.dataset.field)
    );

    // A field can append a unit to its summary entry, so the line reads
    // "5–6 Reformers" rather than a bare "5–6".
    const suffixFor = (field) => {
      const el = this.querySelector(`[data-field="${CSS.escape(field)}"][data-summary-suffix]`);
      // Shopify trims text settings, so a leading space here would be lost.
      // The space is added when joining instead.
      return ((el && el.dataset.summarySuffix) || '').trim();
    };

    // Where a chip writes both a numeric value and a readable band, the summary
    // should show the band: "4-6 Reformers" reads better than "4 Reformers".
    const readable = (field) => {
      const el = this.querySelector(`[data-field="${CSS.escape(field)}"][data-label-field]`);
      const companion = el && el.dataset.labelField;
      if (companion && this.answers[companion] && this.answers[companion].length) {
        return this.answers[companion];
      }
      return this.answers[field];
    };

    const parts = [];
    fields.forEach((field) => {
      const answer = readable(field);
      if (!answer || !answer.length) return;
      const suffix = suffixFor(field);

      if (!Array.isArray(answer)) {
        parts.push(suffix ? `${answer} ${suffix}` : answer);
        return;
      }

      // List short multi-answers, collapse long ones so the line stays readable.
      parts.push(answer.length > 2 ? `${answer.length} selected` : answer.join(', '));
    });

    this.summary.textContent = parts.join(' · ');
  }

  /* ----------------------------------------------------------- submission */

  async onSubmit(event) {
    event.preventDefault();
    // Enter (or Go on a phone keyboard) in an earlier step submits the form
    // natively. Treat it as Continue, so no step is skipped and step 1 is
    // still captured, rather than sending a quote with only contact details.
    if (this.index < this.steps.length - 1) {
      this.next();
      return;
    }
    this.syncTextAnswers();
    if (!this.validateStep()) return;

    const submitButton = this.currentStep.querySelector('[type="submit"]');
    if (submitButton) submitButton.disabled = true;

    if (!this.portalId || !this.formGuid) {
      console.warn('[commercial-quote-builder] HubSpot portal ID or form GUID missing, using email fallback.');
      return this.submitFallback();
    }

    try {
      await this.submitToHubSpot();
      this.announceSubmit('hubspot');
      this.showSuccess();
    } catch (error) {
      console.error('[commercial-quote-builder]', error);
      if (this.errorMessage) this.showError(this.errorMessage);
      this.submitFallback();
    }
  }

  /** HubSpot ties a submission to the visitor's analytics session via this cookie. */
  get hubspotCookie() {
    const match = document.cookie.match(/(?:^|;\s*)hubspotutk=([^;]*)/);
    return match ? match[1] : '';
  }

  /** Human label for a field key, for the overflow blob and the email fallback. */
  labelFor(key) {
    const el = this.querySelector(`[data-field="${CSS.escape(key)}"][data-label]`);
    return (el && el.dataset.label) || key;
  }

  /**
   * Split answers into real HubSpot properties and everything else.
   *
   * A block with no property name gets a synthetic "__of:" key in Liquid. Those
   * are collected into a single long-text property so a merchant can add a
   * question in the customizer without creating a matching HubSpot property
   * first — otherwise HubSpot rejects the entire submission.
   */
  buildFields() {
    const fields = [];
    const overflow = [];

    Object.entries(this.answers).forEach(([name, value]) => {
      if (!value || !value.length) return;
      const flat = Array.isArray(value) ? value.join('; ') : value;

      if (name.startsWith('__of:')) {
        overflow.push(`${this.labelFor(name)}: ${flat}`);
      } else {
        fields.push({ name, value: flat });
      }
    });

    if (overflow.length && this.overflowProperty) {
      fields.push({ name: this.overflowProperty, value: overflow.join('\n') });
    }

    return fields;
  }

  submitToHubSpot() {
    return this.postToHubSpot(this.formGuid, this.buildFields());
  }

  /**
   * Early lead capture.
   *
   * The contact details on a step go to HubSpot as soon as the buyer moves past
   * it, so a quote abandoned halfway still leaves a name and email to follow
   * up. They go to the separate lead form when one is set, otherwise to the
   * quote form itself, marked as step 1 only and carrying the quote reference,
   * so the full quote that may follow can be matched to it. Sent once per set
   * of details, never holds up the next step, and a failure only logs.
   */
  captureLead(step) {
    const guid = this.leadFormGuid || this.formGuid;
    if (!this.portalId || !guid) return;

    const names = new Set(Array.from(step.querySelectorAll('[data-field]')).map((el) => el.dataset.field));
    const fields = [];
    names.forEach((name) => {
      // Questions with no HubSpot property only exist in the full form's
      // overflow field, which the lead form does not have.
      if (name.startsWith('__of:')) return;
      const value = this.answers[name];
      if (!value || !value.length) return;
      fields.push({ name, value: Array.isArray(value) ? value.join('; ') : value });
    });

    // HubSpot files a contact by email, so without one there is nothing to follow up.
    if (!fields.some((field) => field.name === 'email')) return;

    // Into the quote form itself: add its hidden fields (entry form and quote
    // reference, which that form already has), with the entry form marked as
    // step 1 only, so the team can tell an unfinished quote from a full one.
    if (!this.leadFormGuid) {
      this.querySelectorAll('input[type="hidden"][data-field]').forEach((input) => {
        const name = input.dataset.field;
        if (name.startsWith('__of:') || !input.value) return;
        const value = name === this.entryField ? `${input.value} (step 1 only)` : input.value;
        fields.push({ name, value });
      });
    }

    const signature = JSON.stringify(fields);
    if (signature === this.leadSent) return;
    this.leadSent = signature;

    this.postToHubSpot(guid, fields, { keepalive: true }).catch((error) => {
      this.leadSent = '';
      console.error('[commercial-quote-builder] early lead capture failed', error);
    });
  }

  /** The consent HubSpot expects with a submission, from the statement or tick box on the form. */
  get consentOptions() {
    // Consent to process is stated rather than ticked, so submitting the form
    // is the consent. A marketing checkbox, where present, is a separate
    // optional opt-in and must never gate the submission.
    const statement = this.querySelector('[data-consent-text]');
    const consentBox = this.querySelector('input[data-consent]');
    if (!statement && !consentBox) return null;

    return {
      consent: {
        consentToProcess: statement ? true : consentBox.checked,
        text: statement
          ? statement.innerText.trim()
          : (consentBox.closest('label') || {}).innerText || 'Consent to process',
      },
    };
  }

  async postToHubSpot(formGuid, fields, { keepalive = false } = {}) {
    const payload = {
      fields,
      context: {
        pageUri: window.location.href,
        pageName: document.title,
      },
    };

    if (this.hubspotCookie) payload.context.hutk = this.hubspotCookie;
    const consent = this.consentOptions;
    if (consent) payload.legalConsentOptions = consent;

    // EU-resident portals submit to api-eu1; posting to the NA host fails.
    const host = this.region === 'eu1' ? 'api-eu1.hsforms.com' : 'api.hsforms.com';

    // keepalive lets an early capture finish even if the buyer closes the tab
    // straight after moving on, which is exactly the case it exists for.
    const response = await fetch(
      `https://${host}/submissions/v3/integration/submit/${this.portalId}/${formGuid}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        keepalive,
      }
    );

    if (!response.ok) {
      const detail = await response.text().catch(() => '');
      throw new Error(`HubSpot responded ${response.status}: ${detail}`);
    }
  }

  /** Lets the tracking layer observe a real submission without reaching inside. */
  announceSubmit(destination) {
    this.dispatchEvent(new CustomEvent('commercial:quote-submitted', {
      bubbles: true,
      detail: { destination },
    }));
  }

  submitFallback() {
    if (!this.fallbackForm) return;
    this.announceSubmit('shopify-contact-fallback');

    const email = this.fallbackForm.querySelector('[data-fallback="email"]');
    const body = this.fallbackForm.querySelector('[data-fallback="body"]');

    if (email) email.value = this.answers.email || '';
    if (body) {
      body.value = Object.entries(this.answers)
        .filter(([, value]) => value && value.length)
        .map(([name, value]) => {
          const flat = Array.isArray(value) ? value.join('; ') : value;
          return `${name.startsWith('__of:') ? this.labelFor(name) : name}: ${flat}`;
        })
        .join('\n');
    }

    this.fallbackForm.submit();
  }

  showSuccess() {
    this.steps.forEach((step) => {
      step.hidden = true;
    });
    if (this.done) {
      const slot = this.done.querySelector('[data-qb-reference]');
      if (slot && this._reference) {
        slot.textContent = this._reference;
        const wrap = this.done.querySelector('[data-qb-ref-wrap]');
        if (wrap) wrap.hidden = false;
      }
      this.done.hidden = false;
      this.done.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }
}

if (!customElements.get('commercial-quote-builder')) {
  customElements.define('commercial-quote-builder', CommercialQuoteBuilder);
}
